import { Copy } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { formatUnknownJson } from '@/services/bigQuerySyncPreview/formatUnknownJson';

const LONG_CHAR_THRESHOLD = 480;
const LONG_LINE_THRESHOLD = 12;

type BigQuerySyncDiffJsonBlockProps = {
  value: unknown;
};

export function BigQuerySyncDiffJsonBlock({ value }: BigQuerySyncDiffJsonBlockProps) {
  const [copied, setCopied] = useState(false);
  const text = formatUnknownJson(value);

  if (!text) {
    return <span className="text-muted-foreground">—</span>;
  }

  const lineCount = text.split('\n').length;
  const charCount = text.length;
  const isLong = charCount > LONG_CHAR_THRESHOLD || lineCount > LONG_LINE_THRESHOLD;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const preClass =
    'font-mono text-xs leading-relaxed whitespace-pre-wrap break-all max-h-[min(22rem,45vh)] overflow-auto rounded-md border border-border/60 bg-background/80 p-2';

  if (!isLong) {
    return (
      <div className="flex flex-col gap-1">
        <pre className={preClass}>{text}</pre>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-fit gap-1 self-end px-2 text-xs"
          onClick={() => void handleCopy()}
        >
          <Copy className="size-3.5" aria-hidden />
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      </div>
    );
  }

  return (
    <details className="group rounded-md border border-border/80 bg-muted/15 open:bg-muted/25">
      <summary className="cursor-pointer list-none px-2 py-2 text-xs font-medium text-foreground marker:hidden [&::-webkit-details-marker]:hidden">
        <span className="text-muted-foreground">JSON</span>
        <span className="ml-1.5 text-muted-foreground">
          ({lineCount} linhas · {charCount} caracteres)
        </span>
        <span className="ml-1 underline-offset-2 group-open:underline"> — expandir</span>
      </summary>
      <div className="flex flex-col gap-2 border-t border-border/60 p-2">
        <pre className={preClass}>{text}</pre>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-8 w-fit gap-1.5 self-end px-3 text-xs"
          onClick={() => void handleCopy()}
        >
          <Copy className="size-3.5" aria-hidden />
          {copied ? 'Copiado para a área de transferência' : 'Copiar JSON completo'}
        </Button>
      </div>
    </details>
  );
}
